
import Page from '@/Page.vue';
import { Component } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';
import TpmFiltersBar from '@/libs/tpm-analytics/components/TpmFiltersBar.vue';
import MissingCycleTimesAlert from '@/libs/tpm-analytics/components/MissingCycleTimesAlert.vue';

import {
    getOeeSummary,
    getMaintenanceSummary,
    getFinancialImpactSummary,
    getOeeDaily,
    OeeDailyPoint,
    getOeeByWorkplace,
    getTopFaultTypes,
    TopFaultTypeItem,
    getFinancialCostsTrendDaily,
    FinancialCostsTrendDay,
} from '@/libs/tpm-analytics/tpm-analytics-data-service';
import {
    getSelectedFilterLocal,
    setSelectedFilterLocal,
    getFilterById,
    unpackSettings,
} from '@/libs/tpm-analytics/core/saved-filters';
import { getDateRange, getPreviousDateRange } from '@/libs/tpm-analytics/core/date-range.utils';
import { resetMissingCycleTimes } from '@/libs/tpm-analytics/core/missing-cycle-times';
import { getOptimalWindowSize } from '@/libs/tpm-analytics/core/chart-zoom-storage';
import { getDayStartMinutes } from '@/libs/tpm-analytics/core/production-day.utils';

@Component({ components: { TpmFiltersBar, MissingCycleTimesAlert } })
export default class TpmAnalyticsDashboard extends Page {
    periodPreset:
        | 'previousDay'
        | 'last7d'
        | 'last14d'
        | 'last30d'
        | 'last3m'
        | 'thisWeek'
        | 'lastWeek'
        | 'thisMonth'
        | 'lastMonth'
        | 'thisQuarter'
        | 'lastQuarter'
        | 'thisYear'
        | 'lastYear'
        | 'custom' = 'last7d';
    customFrom: string | null = null;
    customTo: string | null = null;
    selectedLocationIds: string[] = [];
    selectedWorkplaceIds: string[] = [];
    selectedProjectIds: string[] = [];
    selectedShiftTypeIds: string[] = [];
    selectedShiftTeamTypeIds: string[] = [];

    // Sdílené uložené filtry – přesunuto do parent (TpmAnalytics.vue)

    // OEE KPI
    oee: number | null = null;
    prevOee: number | null = null;
    deltaOeePct: number | null = null;
    // OEE meta
    oeeShiftCount = 0;
    oeeOk = 0;
    oeeNok = 0;
    oeePlan = 0;
    oeeXMin = 0;
    oeeUnplannedMin = 0;
    availability: number | null = null;
    prevAvailability: number | null = null;
    deltaAvailabilityPct: number | null = null;
    quality: number | null = null;
    prevQuality: number | null = null;
    deltaQualityPct: number | null = null;
    performance: number | null = null;
    prevPerformance: number | null = null;
    deltaPerformancePct: number | null = null;

    // Maintenance KPI
    mtbfHours: number | null = null;
    prevMtbfHours: number | null = null;
    deltaMtbfPct: number | null = null;
    mttrHours: number | null = null;
    prevMttrHours: number | null = null;
    deltaMttrPct: number | null = null;
    failuresCount = 0;
    mtbfSamplesCount = 0;
    totalDowntimeMin = 0;

    // Financial KPI
    downtimeCostsKc = 0;
    prevDowntimeCostsKc = 0;
    deltaDowntimePct: number | null = null;
    scrapCostsKc = 0;
    prevScrapCostsKc = 0;
    deltaScrapPct: number | null = null;
    scrapDataCompletenessPercent: number | null = null;
    unionDowntimeMinutes = 0;
    lostProfitKc = 0;

    // Trend OEE
    daily: OeeDailyPoint[] = [];
    trendViewStart: number | null = null;
    trendViewEnd: number | null = null;
    // Pro kategorickou osu: indexy viditelných kategorií
    trendCategoryStartIndex: number | null = null;
    trendCategoryEndIndex: number | null = null;
    oeeGranularity: 'day' | 'week' | 'month' = 'day';
    // Reentrancy guard to avoid duplicate concurrent loads
    private isLoadingFilters = false;
    private pendingFiltersRun = false;
    applyingInitial = false;
    private filtersChangeTimer: any = null;

    

    // Lowest OEE workplaces (with custom zoom/pan)
    wpBarOptions: any = { xaxis: { categories: [] } };
    wpBarSeries: any[] = [{ name: '', data: [] }];
    wpAllNames: string[] = [];
    wpAllValues: number[] = [];
    wpStartIndex: number | null = null;
    wpEndIndex: number | null = null;

    // Top fault types donut
    faultsDonutOptions: any = { labels: [] };
    faultsDonutSeries: number[] = [];

    // Costs trend stacked
    costsDaily: FinancialCostsTrendDay[] = [];
    costsViewStart: number | null = null;
    costsViewEnd: number | null = null;
    costsWindowDays = 7;
    costsGranularity: 'day' | 'week' | 'month' = 'day';

    // UI stav pro uložené filtry je spravován v parent komponentě

    get periodItems() {
        return [
            { text: this.$t('tpmFilters.previousDay') as any, value: 'previousDay' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.last7d') as any, value: 'last7d' },
            { text: this.$t('tpmFilters.last14d') as any, value: 'last14d' },
            { text: this.$t('tpmFilters.last30d') as any, value: 'last30d' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.preset7d') as any, value: '7d' },
            { text: this.$t('tpmFilters.preset1m') as any, value: '1m' },
            { text: this.$t('tpmFilters.preset3m') as any, value: '3m' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.thisWeek') as any, value: 'thisWeek' },
            { text: this.$t('tpmFilters.lastWeek') as any, value: 'lastWeek' },
            { text: this.$t('tpmFilters.thisMonth') as any, value: 'thisMonth' },
            { text: this.$t('tpmFilters.lastMonth') as any, value: 'lastMonth' },
            { text: this.$t('tpmFilters.thisQuarter') as any, value: 'thisQuarter' },
            { text: this.$t('tpmFilters.lastQuarter') as any, value: 'lastQuarter' },
            { text: this.$t('tpmFilters.thisYear') as any, value: 'thisYear' },
            { text: this.$t('tpmFilters.lastYear') as any, value: 'lastYear' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.presetCustom') as any, value: 'custom' },
        ];
    }

    // savedFilterItems spravuje parent

    getRange() {
        return getDateRange({
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            granularity: this.oeeGranularity,
        });
    }

    onFiltersChanged() {
        if (this.isLoadingFilters) {
            this.pendingFiltersRun = true;
            return;
        }
        this.isLoadingFilters = true;
        // Reset missing cycle times na začátku celého načítání
        resetMissingCycleTimes();
        const { from, to } = this.getRange();
        const filters = {
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        } as any;

        // POZNÁMKA: Default granularitu nastavuje TpmFiltersBar v onPeriodChanged()
        // Zde NEPŘEPISUJEME granularitu, aby se zachovala manuální změna uživatele

        const promises: Array<Promise<any>> = [];
        promises.push(
        getOeeSummary({ from, to, ...filters }).then(async (cur: any) => {
            const { from: prevFrom, to: prevTo } = getPreviousDateRange({
                periodPreset: this.periodPreset,
                customFrom: this.customFrom,
                customTo: this.customTo,
                granularity: this.oeeGranularity,
            });
            const prev = await getOeeSummary({ from: prevFrom, to: prevTo, ...filters });
            this.availability = cur.availability;
            this.prevAvailability = prev.availability;
            this.quality = cur.quality;
            this.prevQuality = prev.quality;
            this.performance = cur.performance as any;
            this.prevPerformance = (prev as any).performance;
            this.oee = cur.oee;
            this.prevOee = prev.oee;
            const m: any = (cur as any).meta || {};
            this.oeeShiftCount = Number(m.shiftCount || 0);
            this.oeeOk = Number(m.totalOk || 0);
            this.oeeNok = Number(m.totalNok || 0);
            this.oeePlan = Number(m.totalPlanByCt || 0);
            this.oeeXMin = Number(m.totalXMinutes || 0);
            this.oeeUnplannedMin = Number(m.totalUnplannedMinutes || 0);
            this.deltaAvailabilityPct =
                cur.availability != null && prev.availability != null && prev.availability > 0
                    ? +(((cur.availability - prev.availability) / prev.availability) * 100).toFixed(1)
                    : null;
            this.deltaQualityPct =
                cur.quality != null && prev.quality != null && prev.quality > 0
                    ? +(((cur.quality - prev.quality) / prev.quality) * 100).toFixed(1)
                    : null;
            this.deltaPerformancePct =
                this.performance != null && this.prevPerformance != null && (this.prevPerformance as number) > 0
                    ? +(
                          (((this.performance as number) - (this.prevPerformance as number)) /
                              (this.prevPerformance as number)) *
                          100
                      ).toFixed(1)
                    : null;
            this.deltaOeePct =
                cur.oee != null && prev.oee != null && prev.oee > 0
                    ? +(((cur.oee - prev.oee) / prev.oee) * 100).toFixed(1)
                    : null;
        }));

        // Maintenance MTBF current + previous
        promises.push((async () => {
            const cur: any = await getMaintenanceSummary(from, to, {
                locationIds: filters.locationIds,
                workplaceIds: filters.workplaceIds,
                projectIds: filters.projectIds,
                shiftTypeIds: filters.shiftTypeIds,
                shiftTeamTypeIds: filters.shiftTeamTypeIds,
            });
            const { from: prevFrom, to: prevTo } = getPreviousDateRange({
                periodPreset: this.periodPreset,
                customFrom: this.customFrom,
                customTo: this.customTo,
            });
            const prev = await getMaintenanceSummary(prevFrom, prevTo, {
                locationIds: filters.locationIds,
                workplaceIds: filters.workplaceIds,
                projectIds: filters.projectIds,
                shiftTypeIds: filters.shiftTypeIds,
                shiftTeamTypeIds: filters.shiftTeamTypeIds,
            });
            this.mtbfHours = cur.mtbfHours;
            this.prevMtbfHours = prev.mtbfHours;
            this.mttrHours = cur.mttrHours;
            this.prevMttrHours = prev.mttrHours;
            this.failuresCount = cur.failuresCount;
            this.totalDowntimeMin = Number((cur.meta && cur.meta.totalDowntimeMin) || 0);
            this.mtbfSamplesCount = Number((cur.meta && cur.meta.mtbfSamplesCount) || 0);
            this.deltaMtbfPct =
                cur.mtbfHours != null && prev.mtbfHours != null && prev.mtbfHours > 0
                    ? +(((cur.mtbfHours - prev.mtbfHours) / prev.mtbfHours) * 100).toFixed(1)
                    : null;
            this.deltaMttrPct =
                cur.mttrHours != null && prev.mttrHours != null && prev.mttrHours > 0
                    ? +(((cur.mttrHours - prev.mttrHours) / prev.mttrHours) * 100).toFixed(1)
                    : null;
        })());

        // Financial costs current + previous
        promises.push(getFinancialImpactSummary(from, to, filters).then((res: any) => {
            this.downtimeCostsKc = Math.round(res.downtimeCostsKc);
            this.scrapCostsKc = Math.round(res.scrapCostsKc);
            this.prevDowntimeCostsKc = Math.round(res.prev?.downtimeCostsKc ?? 0);
            this.prevScrapCostsKc = Math.round(res.prev?.scrapCostsKc ?? 0);
            this.deltaDowntimePct =
                this.prevDowntimeCostsKc > 0
                    ? +(((this.downtimeCostsKc - this.prevDowntimeCostsKc) / this.prevDowntimeCostsKc) * 100).toFixed(1)
                    : null;
            this.deltaScrapPct =
                this.prevScrapCostsKc > 0
                    ? +(((this.scrapCostsKc - this.prevScrapCostsKc) / this.prevScrapCostsKc) * 100).toFixed(1)
                    : null;
            this.unionDowntimeMinutes = Number((res.meta && res.meta.unionDowntimeMinutes) || 0);
            this.lostProfitKc = Math.round(res.lostProfitKc || 0);
            this.scrapDataCompletenessPercent = res.meta?.scrapDataCompleteness?.completenessPercent ?? null;
        }));

        // OEE daily for trend
        promises.push(getOeeDaily({ from, to, ...filters, granularity: this.oeeGranularity } as any).then((rows: any) => {
            this.daily = rows;
            this.resetTrendViewport();
        }));

        // Workplaces with lowest OEE (bottom 4)
        promises.push(getOeeByWorkplace({ from, to, ...filters } as any).then((rows: any) => {
            const sorted = [...rows].sort((a, b) => (a.oee ?? 1) - (b.oee ?? 1));
            this.wpAllNames = sorted.map((x) => x.workplaceName);
            this.wpAllValues = sorted.map((x) => (x.oee ?? 0) * 100.0);
            this.wpBarOptions = {
                chart: { id: 'lowest-oee', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } },
                xaxis: { categories: [], labels: { rotate: 0 } },
                yaxis: {
                    min: 0,
                    max: 100,
                    labels: { formatter: (v: any) => (isFinite(Number(v)) ? Number(v).toFixed(1) + '%' : '') },
                },
                plotOptions: { bar: { columnWidth: '55%', borderRadius: 4 } },
                fill: {
                    type: 'gradient',
                    gradient: { shade: 'light', shadeIntensity: 0.06, opacityFrom: 0.9, opacityTo: 0.85, stops: [0, 80, 100] },
                },
                dataLabels: { enabled: false },
                legend: { show: false },
            };
            this.resetWpViewport();
        }));

        // Top fault types donut (counts) - TOP 10 + Ostatní
        promises.push(getTopFaultTypes(from, to, 999, { locationIds: filters.locationIds, workplaceIds: filters.workplaceIds }).then(
            (items: TopFaultTypeItem[]) => {
                const totalCount = items.reduce((sum, item) => sum + (item.count ?? 0), 0);
                const top10 = items.slice(0, 10);
                const top10Count = top10.reduce((sum, item) => sum + (item.count ?? 0), 0);
                const othersCount = totalCount - top10Count;
                
                const labels = top10.map((i) => {
                    const percentage = totalCount > 0 ? ((i.count / totalCount) * 100).toFixed(1) : '0.0';
                    return `${i.typeName} (${percentage}%)`;
                });
                const data = top10.map((i) => i.count);
                
                // Přidat "Ostatní", pokud existují
                if (othersCount > 0) {
                    const othersPercentage = totalCount > 0 ? ((othersCount / totalCount) * 100).toFixed(1) : '0.0';
                    labels.push(`${this.$t('tpmAnalytics.others')} (${othersPercentage}%)`);
                    data.push(othersCount);
                }
                
                this.faultsDonutOptions = {
                    chart: { id: 'top-fault-types', toolbar: { show: false } },
                    labels,
                    legend: { position: 'right' },
                    stroke: { width: 1 },
                    plotOptions: { pie: { donut: { size: '60%' } } },
                    fill: {
                        type: 'gradient',
                        gradient: {
                            shade: 'light',
                            shadeIntensity: 0.06,
                            opacityFrom: 0.9,
                            opacityTo: 0.85,
                            stops: [0, 80, 100],
                        },
                    },
                };
                this.faultsDonutSeries = data;
            }
        ));

        // Costs trend daily (repairs, downtime, maintenance, scrap)
        promises.push(getFinancialCostsTrendDaily(from, to, filters).then((list) => {
            this.costsDaily = list;
            this.resetCostsViewport();
        }));

        Promise.all(promises).finally(() => {
            const fn = (this.$root as any).$tpmOnStateChanged;
            if (typeof fn === 'function') fn();
            this.isLoadingFilters = false;
            if (this.pendingFiltersRun) {
                this.pendingFiltersRun = false;
                this.onFiltersChanged();
            }
        });
    }

    // Uložené filtry – obsluha přesunuta do parent komponenty
    resetWpViewport() {
        const n = this.wpAllNames.length;
        if (n === 0) {
            this.wpStartIndex = this.wpEndIndex = null;
            this.wpBarOptions = { ...this.wpBarOptions, xaxis: { ...(this.wpBarOptions.xaxis || {}), categories: [] } };
            this.wpBarSeries = [{ name: this.$t('tpmAnalytics.oeeShort') as any, data: [] }];
            return;
        }
        // Zobrazit VŠECHNA pracoviště najednou
        this.wpStartIndex = 0;
        this.wpEndIndex = n - 1;
        this.applyWpSlice();
    }
    applyWpSlice() {
        if (this.wpStartIndex == null || this.wpEndIndex == null) return;
        const cats = this.wpAllNames.slice(this.wpStartIndex, this.wpEndIndex + 1);
        const vals = this.wpAllValues.slice(this.wpStartIndex, this.wpEndIndex + 1);
        this.wpBarOptions = { ...this.wpBarOptions, xaxis: { ...(this.wpBarOptions.xaxis || {}), categories: cats } };
        this.wpBarSeries = [{ name: this.$t('tpmAnalytics.oeeShort') as any, data: vals }];
    }
    wpShiftPercent(percent: number) {
        if (this.wpStartIndex == null || this.wpEndIndex == null) return;
        const span = this.wpEndIndex - this.wpStartIndex + 1;
        const n = this.wpAllNames.length;
        const delta = Math.max(1, Math.round(span * Math.abs(percent)));
        let start = this.wpStartIndex + (percent > 0 ? delta : -delta);
        start = Math.max(0, Math.min(start, Math.max(0, n - span)));
        this.wpStartIndex = start;
        this.wpEndIndex = start + span - 1;
        this.applyWpSlice();
    }
    wpZoomIn() {
        if (this.wpStartIndex == null || this.wpEndIndex == null) return;
        const n = this.wpAllNames.length;
        const span = this.wpEndIndex - this.wpStartIndex + 1;
        const newSpan = Math.max(2, Math.round(span * 0.75));
        let start = this.wpStartIndex;
        if (start + newSpan > n) start = Math.max(0, n - newSpan);
        this.wpStartIndex = start;
        this.wpEndIndex = start + newSpan - 1;
        this.applyWpSlice();
    }
    wpZoomOut() {
        if (this.wpStartIndex == null || this.wpEndIndex == null) return;
        const n = this.wpAllNames.length;
        const span = this.wpEndIndex - this.wpStartIndex + 1;
        const newSpan = Math.min(n, Math.round(span / 0.75));
        let start = this.wpStartIndex;
        if (start + newSpan > n) start = Math.max(0, n - newSpan);
        this.wpStartIndex = start;
        this.wpEndIndex = start + newSpan - 1;
        this.applyWpSlice();
    }

    $watch!: any;
    created() {
        // Registrace rozhraní pro parent – získání a aplikace stavu filtrů
        (this.$root as any).$tpmGetCurrentState = this.getCurrentFilterState.bind(this);
        (this.$root as any).$tpmApplyState = this.applyFilterState.bind(this);
        // Preferuj URL query -> vybraný filtr -> localStorage
        const initState = (this.$root as any).$tpmInitialState;
        const initSid = (this.$root as any).$tpmInitialSid;
        if (initState && Object.keys(initState || {}).length > 0) {
            this.applyFilterState(initState, false);
            return;
        }
        if (initSid) {
            setSelectedFilterLocal(initSid);
            getFilterById(initSid).then((model) => {
                const state = unpackSettings(model?.settings);
            this.applyFilterState(state, false);
            });
            return;
        }
        const localId = getSelectedFilterLocal();
        if (localId) {
            setSelectedFilterLocal(localId);
            getFilterById(localId).then((model) => {
                const state = unpackSettings(model?.settings);
                this.applyFilterState(state, false);
            });
        }
        // Pokud není žádný initial state, mounted() zavolá load() s defaultními hodnotami
    }

    mounted() {
        // V mounted() VŽDY voláme load() - created() pouze nastavil stav
        this.onFiltersChanged();
        
        // Watch registrujeme až v mounted(), aby se nespustil při async načítání initial state v created()
        this.$watch('oeeGranularity', () => {
            this.scheduleFiltersChanged();
        });
    }

    destroyed() {
        if ((this.$root as any).$tpmGetCurrentState === this.getCurrentFilterState)
            delete (this.$root as any).$tpmGetCurrentState;
        if ((this.$root as any).$tpmApplyState === this.applyFilterState) delete (this.$root as any).$tpmApplyState;
    }

    getCurrentFilterState() {
        return {
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            selectedLocationIds: this.selectedLocationIds,
            selectedWorkplaceIds: this.selectedWorkplaceIds,
            selectedProjectIds: this.selectedProjectIds,
            selectedShiftTypeIds: this.selectedShiftTypeIds,
            selectedShiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            granularity: this.oeeGranularity,
        } as any;
    }
    applyFilterState(state: any, reload = true) {
        // reload = true: uživatel změnil filtry → načti data
        // reload = false: inicializace v created() → pouze nastav stav, data se načtou v mounted()
        this.applyingInitial = true;
        if (state?.periodPreset) this.periodPreset = state.periodPreset;
        if (state?.customFrom) this.customFrom = state.customFrom;
        if (state?.customTo) this.customTo = state.customTo;
        this.selectedLocationIds = Array.isArray(state?.selectedLocationIds) ? state.selectedLocationIds : [];
        this.selectedWorkplaceIds = Array.isArray(state?.selectedWorkplaceIds) ? state.selectedWorkplaceIds : [];
        this.selectedProjectIds = Array.isArray(state?.selectedProjectIds) ? state.selectedProjectIds : [];
        this.selectedShiftTypeIds = Array.isArray(state?.selectedShiftTypeIds) ? state.selectedShiftTypeIds : [];
        this.selectedShiftTeamTypeIds = Array.isArray(state?.selectedShiftTeamTypeIds) ? state.selectedShiftTeamTypeIds : [];
        if (state?.granularity) {
            this.oeeGranularity = state.granularity;
            this.costsGranularity = state.granularity;
        } else if (state?.oeeGranularity) {
            // Zpětná kompatibilita se starými uloženými filtry
            this.oeeGranularity = state.oeeGranularity;
            this.costsGranularity = state.costsGranularity || state.oeeGranularity;
        }
        this.applyingInitial = false;
        if (reload) {
            this.scheduleFiltersChanged();
        }
    }

    scheduleFiltersChanged() {
        // Debounce 200ms - zabráníme duplicitnímu volání při rychlé změně více filtrů
        if (this.filtersChangeTimer) clearTimeout(this.filtersChangeTimer);
        this.filtersChangeTimer = setTimeout(() => {
            this.filtersChangeTimer = null;
            this.onFiltersChanged();
        }, 200);
    }

    // Period captions
    get currentPeriodText() {
        const { from, to } = this.getRange();
        const fmt = (v: string) => moment.tz(v, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm');
        return `${fmt(from)} – ${fmt(to)}`;
    }
    get previousPeriodText() {
        const { from: prevFrom, to: prevTo } = getPreviousDateRange({
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            granularity: this.oeeGranularity,
        });
        const fmt = (v: string) => moment.tz(v, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm');
        return `${fmt(prevFrom)} – ${fmt(prevTo)}`;
    }

    // Formatting
    formatPct(v: number | null): string {
        if (v == null || isNaN(v as any)) return '—';
        return ((v as number) * 100).toFixed(1) + '%';
    }
    formatDelta(p: number | null): string {
        if (p == null || isNaN(p as any)) return '—';
        const s = (p as number) >= 0 ? '+' : '';
        return s + (p as number).toFixed(1) + '%';
    }
    formatHours(v: number | null): string {
        if (v == null || isNaN(v as any)) return '—';
        return `${(v as number).toFixed(1)}h`;
    }
    formatCurrency(v: number | null): string {
        const num = Math.round(Number(v ?? 0));
        const tenant = (window as any).TenantSettings?.currency ?? 'CZK';
        const locale = (this.$i18n?.locale as any) || 'cs-CZ';
        const map: any = { CZK: 'Kč', EUR: '€', USD: '$', GBP: '£' };
        const symbol = map[tenant] || map.CZK;
        return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(num) + ' ' + symbol;
    }
    formatNum(v: number | null | undefined): string {
        const n = Math.round(Number(v ?? 0));
        const locale = (this.$i18n?.locale as any) || 'cs-CZ';
        return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
    }

    // Costs stacked chart
    get costsOptions() {
        const self = this;
        return {
            chart: {
                id: 'dashboard-costs-trend',
                stacked: true,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
            },
            xaxis: {
                type: 'datetime',
                min: this.costsViewStart ?? undefined,
                max: this.costsViewEnd ?? undefined,
                labels: {
                    rotate: self.costsGranularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        const n = Number(value || 0);
                        return isFinite(n) ? self.formatAxisLabel(n, self.costsGranularity) : '';
                    },
                },
            },
            yaxis: {
                labels: {
                    formatter: (v: any) =>
                        isFinite(Number(v))
                            ? new Intl.NumberFormat((self.$i18n?.locale as any) || 'cs-CZ', {
                                  maximumFractionDigits: 0,
                              }).format(Number(v))
                            : '',
                },
            },
            plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
            dataLabels: { enabled: false },
            fill: {
                type: 'gradient',
                gradient: { shade: 'light', shadeIntensity: 0.06, opacityFrom: 0.9, opacityTo: 0.85, stops: [0, 80, 100] },
            },
            colors: ['#ef4444', '#f59e0b', '#10b981', '#3b82f6'],
            legend: { show: true, position: 'top', horizontalAlign: 'center' },
            tooltip: {
                shared: true,
                intersect: false,
                inverseOrder: true,
                x: { formatter: (val: any) => self.formatAxisLabel(Number(val), self.costsGranularity) as any },
                y: { formatter: (val: number) => self.formatCurrency(val) },
            },
        } as any;
    }
    get costsSeries() {
        const toPt = (val: number, key: string) => {
            const ts = Date.parse(key as any);
            if (!isFinite(ts)) return null;
            return { x: ts, y: Math.round(val) } as any;
        };
        // aggregate according to granularity
        const grouped = new Map<string, { repair: number; downtime: number; maintenance: number; scrap: number }>();
        const getKey = (d: string) => {
            if (this.costsGranularity === 'month') return moment(d).startOf('month').format('YYYY-MM-01');
            if (this.costsGranularity === 'week') return moment(d).startOf('isoWeek').format('YYYY-MM-DD');
            return moment(d).startOf('day').format('YYYY-MM-DD');
        };
        for (const r of this.costsDaily) {
            const k = getKey(r.day);
            const e = grouped.get(k) || { repair: 0, downtime: 0, maintenance: 0, scrap: 0 };
            e.repair += r.repairKc;
            e.downtime += r.downtimeKc;
            e.maintenance += r.maintenanceKc;
            e.scrap += r.scrapKc;
            grouped.set(k, e);
        }
        const keys = Array.from(grouped.keys()).sort();
        const rep = keys.map((k) => toPt(grouped.get(k)!.repair, k)).filter((p) => p != null);
        const down = keys.map((k) => toPt(grouped.get(k)!.downtime, k)).filter((p) => p != null);
        const maint = keys.map((k) => toPt(grouped.get(k)!.maintenance, k)).filter((p) => p != null);
        const scrap = keys.map((k) => toPt(grouped.get(k)!.scrap, k)).filter((p) => p != null);
        return [
            { name: this.$t('tpmAnalytics.fiRepairsLabel') as any, type: 'column', data: rep },
            { name: this.$t('tpmAnalytics.fiDowntimeLabel') as any, type: 'column', data: down },
            { name: this.$t('tpmAnalytics.fiMaintenanceLabel') as any, type: 'column', data: maint },
            { name: this.$t('tpmAnalytics.fiScrapLabel') as any, type: 'column', data: scrap },
        ] as any;
    }
    get oeeGranularityItems() {
        const { from, to } = this.getRange();
        const spanDays = Math.max(1, moment(to).diff(moment(from), 'days'));
        const items: any[] = [
            { text: this.$t('tpmAnalytics.granularityDay'), value: 'day', disabled: spanDays >= 30 },
            { text: this.$t('tpmAnalytics.granularityWeek'), value: 'week', disabled: spanDays >= 365 },
            { text: this.$t('tpmAnalytics.granularityMonth'), value: 'month', disabled: false },
        ];
        return items;
    }
    get costsGranularityItems() {
        return this.oeeGranularityItems;
    }
    resetCostsViewport() {
        if (this.costsDaily.length === 0) {
            this.costsViewStart = null;
            this.costsViewEnd = null;
            return;
        }
        const last = moment(this.costsDaily[this.costsDaily.length - 1].day).endOf('day');
        const visible = Math.min(this.costsWindowDays, this.costsDaily.length);
        this.costsViewEnd = last.valueOf();
        this.costsViewStart = last
            .clone()
            .add(-(visible - 1), 'days')
            .startOf('day')
            .valueOf();
    }
    costsShiftPercent(percent: number) {
        if (this.costsViewStart == null || this.costsViewEnd == null || this.costsDaily.length === 0) return;
        const totalStart = moment(this.costsDaily[0].day).startOf('day').valueOf();
        const totalEnd = moment(this.costsDaily[this.costsDaily.length - 1].day)
            .endOf('day')
            .valueOf();
        const span = this.costsViewEnd - this.costsViewStart;
        const delta = Math.round(span * percent);
        let ns = this.costsViewStart + delta;
        let ne = this.costsViewEnd + delta;
        if (ns < totalStart) {
            ns = totalStart;
            ne = totalStart + span;
        }
        if (ne > totalEnd) {
            ne = totalEnd;
            ns = totalEnd - span;
        }
        this.costsViewStart = ns;
        this.costsViewEnd = ne;
    }
    costsZoomIn() {
        if (this.costsViewStart == null || this.costsViewEnd == null || this.costsDaily.length === 0) return;
        const totalStart = moment(this.costsDaily[0].day).startOf('day').valueOf();
        const totalEnd = moment(this.costsDaily[this.costsDaily.length - 1].day)
            .endOf('day')
            .valueOf();
        const span = this.costsViewEnd - this.costsViewStart;
        const minSpan = 2 * 24 * 60 * 60 * 1000;
        const newSpan = Math.max(minSpan, Math.round(span * 0.75));
        const center = Math.round((this.costsViewStart + this.costsViewEnd) / 2);
        let ns = center - Math.round(newSpan / 2);
        let ne = center + Math.round(newSpan / 2);
        if (ns < totalStart) {
            ns = totalStart;
            ne = ns + newSpan;
        }
        if (ne > totalEnd) {
            ne = totalEnd;
            ns = ne - newSpan;
        }
        this.costsViewStart = ns;
        this.costsViewEnd = ne;
    }
    costsZoomOut() {
        if (this.costsViewStart == null || this.costsViewEnd == null || this.costsDaily.length === 0) return;
        const totalStart = moment(this.costsDaily[0].day).startOf('day').valueOf();
        const totalEnd = moment(this.costsDaily[this.costsDaily.length - 1].day)
            .endOf('day')
            .valueOf();
        const span = this.costsViewEnd - this.costsViewStart;
        const newSpan = Math.min(totalEnd - totalStart, Math.round(span / 0.75));
        const center = Math.round((this.costsViewStart + this.costsViewEnd) / 2);
        let ns = center - Math.round(newSpan / 2);
        let ne = center + Math.round(newSpan / 2);
        if (ns < totalStart) {
            ns = totalStart;
            ne = ns + newSpan;
        }
        if (ne > totalEnd) {
            ne = totalEnd;
            ns = ne - newSpan;
        }
        this.costsViewStart = ns;
        this.costsViewEnd = ne;
    }

    // Agregovaná data pro trend chart (podle granularity)
    get trendAggregatedDataForChart(): OeeDailyPoint[] {
        if (this.oeeGranularity === 'day') {
            return this.daily;
        }

        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;

        const grouped = new Map<number, OeeDailyPoint[]>();

        this.daily.forEach((d) => {
            let periodKey: number;
            if (this.oeeGranularity === 'week') {
                periodKey = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else {
                periodKey = moment(d.day).startOf('month').valueOf() + dayStartMs;
            }

            if (!grouped.has(periodKey)) {
                grouped.set(periodKey, []);
            }
            grouped.get(periodKey)!.push(d);
        });

        const aggregated: OeeDailyPoint[] = [];
        grouped.forEach((points, periodKey) => {
            const avg = (values: (number | null)[]): number | null => {
                const valid = values.filter((v) => v != null && isFinite(v as any)) as number[];
                if (valid.length === 0) return null;
                return valid.reduce((sum, v) => sum + v, 0) / valid.length;
            };

            aggregated.push({
                day: moment(periodKey).toISOString(),
                oee: avg(points.map((p) => p.oee)),
                availability: avg(points.map((p) => p.availability)),
                performance: avg(points.map((p) => p.performance)),
                quality: avg(points.map((p) => p.quality)),
            });
        });

        return aggregated.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }

    // Trend chart helpers (OEE daily)
    get trendOptions() {
        const self = this;
        
        // Vygenerujeme kategorie (labely) pro osu X
        const categories = this.trendAggregatedDataForChart.map((d) => {
            const dayStartMinutes = getDayStartMinutes();
            const dayStartMs = dayStartMinutes * 60 * 1000;
            let timestamp: number;
            
            if (this.oeeGranularity === 'week') {
                timestamp = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.oeeGranularity === 'month') {
                timestamp = moment(d.day).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment(d.day).startOf('day').valueOf() + dayStartMs;
            }
            
            return self.formatAxisLabel(timestamp, self.oeeGranularity);
        });

        return {
            chart: {
                id: 'dashboard-oee-trend',
                type: 'line',
                stacked: false,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
            },
            stroke: { width: [3, 0, 0, 0], curve: 'smooth' },
            markers: { size: 4, hover: { size: 6 } },
            dataLabels: { enabled: false },
            xaxis: {
                type: 'category',
                categories: categories,
                // Pro kategorickou osu je max EXCLUSIVE, takže musíme přičíst 1
                // Pro min používáme -0.8 když začínáme od 0, aby se sloupec maximálně přiblížil k levému okraji
                min: this.trendCategoryStartIndex === 0 ? -0.8 : (this.trendCategoryStartIndex != null ? this.trendCategoryStartIndex - 0.3 : undefined),
                max: this.trendCategoryEndIndex != null ? this.trendCategoryEndIndex + 1.3 : undefined,
                labels: {
                    rotate: self.oeeGranularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        // Pro týdny rozdělíme label na dva řádky pomocí |
                        if (self.oeeGranularity === 'week' && typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts; // ApexCharts podporuje pole pro víceřádkové labely
                        }
                        return value;
                    },
                    style: {
                        cssClass: 'apexcharts-xaxis-label-multiline',
                    },
                },
                axisBorder: {
                    show: true
                },
                axisTicks: {
                    show: true
                }
            },
            yaxis: {
                labels: { formatter: (v: any) => (isFinite(Number(v)) ? Number(v).toFixed(0) + '%' : '') },
                min: 0,
                max: 100,
            },
            plotOptions: {
                bar: {
                    columnWidth: '95%',
                    distributed: false,
                    rangeBarGroupRows: false,
                    horizontal: false,
                    endingShape: 'flat',
                    borderRadius: 4,
                    dataLabels: {
                        position: 'top',
                    }
                }
            },
            states: {
                hover: {
                    filter: {
                        type: 'lighten',
                        value: 0.15,
                    }
                },
                active: {
                    allowMultipleDataPointsSelection: true,
                    filter: {
                        type: 'darken',
                        value: 0.35,
                    }
                }
            },
            grid: {
                padding: {
                    right: 5,
                    // Když začínáme od 0 při zoom, snížíme padding vlevo
                    left: this.trendCategoryStartIndex === 0 ? 0 : 10,
                }
            },
            fill: {
                type: 'gradient',
                gradient: { shade: 'light', shadeIntensity: 0.06, opacityFrom: 0.9, opacityTo: 0.85, stops: [0, 80, 100] },
            },
            colors: ['#2196F3', '#4CAF50', '#FF9800', '#F44336'], // OEE-modrá, A-zelená, E-oranžová, Q-červená
            legend: { show: true, position: 'top', horizontalAlign: 'center' },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                y: {
                    formatter: (val: number) => {
                        if (val == null || isNaN(val)) return '-';
                        return val.toFixed(1) + '%';
                    }
                }
            },
        } as any;
    }
    get trendSeries() {
        // Pro kategorickou osu vracíme pouze hodnoty y (bez x)
        const toValue = (v: number | null): number | null => {
            return v == null || isNaN(v as any) ? null : Number((Math.max(0, Math.min(1, Number(v))) * 100).toFixed(3));
        };

        const oee = this.trendAggregatedDataForChart.map((d) => toValue(d.oee));
        const a = this.trendAggregatedDataForChart.map((d) => toValue(d.availability));
        const e = this.trendAggregatedDataForChart.map((d) => toValue(d.performance));
        const q = this.trendAggregatedDataForChart.map((d) => toValue(d.quality));

        return [
            { name: this.$t('tpmAnalytics.oeeShort') as any, data: oee, type: 'line' },
            { name: this.$t('tpmAnalytics.availability') as any, data: a, type: 'column' },
            { name: this.$t('tpmAnalytics.performance') as any, data: e, type: 'column' },
            { name: this.$t('tpmAnalytics.quality') as any, data: q, type: 'column' },
        ] as any;
    }
    formatDay(ts: number): string {
        return moment(ts).format('D.M.');
    }
    formatAxisLabel(ts: number, gran: 'day' | 'week' | 'month', forTooltip = false): string {
        const m = moment(ts);
        if (!m.isValid()) return '';
        if (gran === 'month') return m.format('MM/YYYY');
        if (gran === 'week') {
            const start = m.clone().startOf('isoWeek');
            const end = m.clone().endOf('isoWeek');
            const week = m.isoWeek();
            const weekLabel = `${week}. týden`;
            const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
            
            // Pro tooltip používáme HTML break
            if (forTooltip) {
                return `${weekLabel}<br/>${dateRange}`;
            }
            // Pro osu X vracíme pole jako string se speciálním separátorem
            // který ApexCharts rozpozná při správném nastavení
            return weekLabel + '|' + dateRange;
        }
        return m.format('D.M.');
    }
    resetTrendViewport() {
        const data = this.trendAggregatedDataForChart;
        if (data.length === 0) {
            this.trendCategoryStartIndex = null;
            this.trendCategoryEndIndex = null;
            return;
        }

        // Pro kategorickou osu: zobrazíme všechna data (null = všechno)
        this.trendCategoryStartIndex = null;
        this.trendCategoryEndIndex = null;
    }
    trendShiftPercent(percent: number) {
        const data = this.trendAggregatedDataForChart;
        if (data.length === 0) return;

        let startIdx: number;
        let endIdx: number;

        // Když jsou indexy null, znamená to zobrazit všechna data
        // Nastavíme výchozí okno a pak posuneme
        if (this.trendCategoryStartIndex == null || this.trendCategoryEndIndex == null) {
            const windowSize = Math.min(10, data.length); // default window size
            
            if (data.length <= windowSize) {
                return; // Málo dat pro zoom
            }

            // Nastavíme výchozí viewport podle směru posunu
            if (percent > 0) {
                // Posun doprava -> začneme od začátku
                startIdx = 0;
                endIdx = windowSize - 1;
            } else {
                // Posun doleva -> začneme od konce
                startIdx = data.length - windowSize;
                endIdx = data.length - 1;
            }
        } else {
            startIdx = this.trendCategoryStartIndex;
            endIdx = this.trendCategoryEndIndex;
        }

        const visibleCount = endIdx - startIdx + 1;
        // Zajistíme minimální posun 1 (aby se graf vždy pohnul, i když je málo kategorií)
        const calculatedShift = visibleCount * percent;
        const shift = percent > 0 
            ? Math.max(1, Math.round(calculatedShift))
            : Math.min(-1, Math.round(calculatedShift));

        let newStart = startIdx + shift;
        let newEnd = endIdx + shift;

        // Bounds check - zachovat počet viditelných kategorií
        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + visibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = newEnd - visibleCount + 1;
            // Pokud by newStart vyšlo záporné, upravíme rozsah
            if (newStart < 0) {
                newStart = 0;
                newEnd = data.length - 1;
            }
        }

        // Zkontrolujeme, jestli se vůbec něco změnilo (můžeme být na kraji)
        if (newStart === startIdx && newEnd === endIdx) {
            return; // Already at boundary
        }

        this.trendCategoryStartIndex = newStart;
        this.trendCategoryEndIndex = newEnd;
    }
    trendZoomIn() {
        const data = this.trendAggregatedDataForChart;
        if (data.length === 0) return;

        const startIdx = this.trendCategoryStartIndex ?? 0;
        const endIdx = this.trendCategoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;
        
        // Přiblížit = zobrazit méně kategorií (75% z aktuálních)
        const minVisible = 2; // minimálně 2 kategorie
        const newVisibleCount = Math.max(minVisible, Math.round(visibleCount * 0.75));
        
        // Zachovat střed viewportu
        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        // Bounds check
        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.trendCategoryStartIndex = newStart;
        this.trendCategoryEndIndex = newEnd;
    }
    trendZoomOut() {
        const data = this.trendAggregatedDataForChart;
        if (data.length === 0) return;

        const startIdx = this.trendCategoryStartIndex ?? 0;
        const endIdx = this.trendCategoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;
        
        // Oddálit = zobrazit více kategorií (133% z aktuálních)
        const maxVisible = data.length;
        const newVisibleCount = Math.min(maxVisible, Math.round(visibleCount / 0.75));

        // Pokud má být viditelné všechno, nastavíme indexy na null
        if (newVisibleCount >= data.length) {
            this.trendCategoryStartIndex = null;
            this.trendCategoryEndIndex = null;
            return;
        }
        
        // Zachovat střed viewportu
        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        // Bounds check
        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.trendCategoryStartIndex = newStart;
        this.trendCategoryEndIndex = newEnd;
    }
}
